import { DataAccess } from "../dataaccess/data.access";
import { S25Util } from "../util/s25-util";
import { PreferenceService } from "./preference.service";

export type PasswordResetOriginatingApp = "connect25";

export type PasswordResetRequestResult = {
    success: boolean;
    message: string;
};

export class PasswordResetService {
    /**
     * Matches 25Live and Connect25 password-reset deep links. Callers must pass the href they mean:
     * the entry URL preserves a deep link across bootstrap, the current URL says where the user is
     * now. The login gate in framework.js exempts the same routes by ui-router state name
     * (passwordReset, sePasswordReset) because it runs after a transition, when a state is known.
     */
    private static readonly passwordResetRoutePattern = /\/password_reset(?:[?#]|$)/i;

    public static isPasswordResetRoute(href: string): boolean {
        return PasswordResetService.passwordResetRoutePattern.test(href);
    }

    public static async isPasswordResetActive(): Promise<boolean> {
        let passwordResetActive = true;
        const [pref, prefErr] = await S25Util.Maybe(PreferenceService.getPreferences(["config_password_reset_active"]));
        if (!!pref && !prefErr) passwordResetActive = S25Util.toBool(pref?.config_password_reset_active?.value);
        return passwordResetActive;
    }

    public static async requestReset(
        username: string,
        options?: { originatingApp?: PasswordResetOriginatingApp },
    ): Promise<PasswordResetRequestResult> {
        //ANG-7065 temporary remove once we've switched to reset only probably 9/26
        const passwordResetActive = await PasswordResetService.isPasswordResetActive();

        let url = "/r25user/remind.json?r25_username=" + encodeURIComponent(username);
        if (options?.originatingApp === "connect25") {
            url += "&originating_app=connect25";
        }
        const [, err] = await S25Util.Maybe(
            DataAccess.post(DataAccess.injectCaller(url, "PasswordResetService.requestReset")),
        );
        return {
            success: !err,
            message: err
                ? passwordResetActive
                    ? "There was a problem resetting your password. Please contact your system administrator."
                    : "There was a problem emailing you your password. Please contact your system administrator."
                : passwordResetActive
                  ? "If an account exists, a password reset link has been sent to the email address on record."
                  : "Your password has been sent to the email address on record.",
        };
    }

    public static completeReset(token: string, password: string) {
        return DataAccess.post(
            DataAccess.injectCaller("/r25user.reset/complete.json", "PasswordResetService.completeReset"),
            { password_reset: { token, password } },
        );
    }
}
